// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import Logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFlyoutDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetFlyoutDrawerIsOpen";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import {
    getCategoriesDataView,
    getCategoryDepthLoaded,
    getCategoryState,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FancyScroll from "@sc/App/Components/Common/FancyScroll";
import FloatingTooltip, { FloatingTooltipStyles } from "@sc/App/Components/FloatingTooltip/FloatingTooltip";
import { xGoogleAnalyticsState } from "@sc/App/GoogleAnalyticsConnector/Store/Reducers/xGoogleAnalytics";
import { getDataLayer } from "@sc/App/GoogleAnalyticsConnector/Utilities/DataLayer";
import * as cssLinter from "css";
import isEqual from "lodash/isEqual";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    links = "links",
    customCSS = "customCSS",
    subLinkList = "subLinkList",
    // XNG-Change
    trackToolBarNavEvent = "trackToolBarNavEvent",
}

const enum subLinkFields {
    subLinkName = "subLinkName",
    subLink = "subLink",
    openInNewWindow = "openInNewWindow",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.links]: LinkModel[];
        [fields.customCSS]: string;
        // XNG-Change
        [fields.trackToolBarNavEvent]: boolean;
    };
    extendedStyles?: HeaderLinkListStyles;
}

// XNG-Change: External URL
interface SubLinkModel {
    fields: {
        [subLinkFields.subLinkName]: string;
        [subLinkFields.subLink]: LinkFieldValue;
        [subLinkFields.openInNewWindow]: boolean;
    };
}

interface LinkModel {
    fields: {
        openInNewWindow: boolean;
        destination: LinkFieldValue;
        overriddenTitle: string;
        // XNG-Changes: Allow for sub-pages
        linkType: "Link" | "CascadingMenu" | "LinkList";
        maxDepth: number;
        [fields.subLinkList]: SubLinkModel[];
    };
}

interface MappedLink {
    title: string;
    url: string;
    type?: string;
    openInNewWindow?: boolean;
    maxDepth?: number;
    childrenType?: "CascadingMenu";
    children?: MappedLink[];
    isCategory?: boolean;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const {
        fields: { links = [] },
    } = ownProps;
    // XNG-Change
    const mappedLinks: MappedLink[] = [];
    const categoryIdsToLoad: Dictionary<number> = {};

    for (const link of links) {
        const {
            fields: { linkType, destination, openInNewWindow, overriddenTitle, maxDepth, subLinkList },
        } = link;
        // XNG-Change
        if (linkType === "LinkList") {
            const subLinks = subLinkList?.map(({ fields: { subLink, subLinkName, openInNewWindow } }: SubLinkModel) => {
                const url = getPageLinkByNodeId(state, subLink.value) || {
                    url: subLink.value,
                    type: subLink.type,
                };
                return {
                    ...url,
                    title: subLinkName,
                    maxDepth: 1,
                    isCategory: false,
                    openInNewWindow,
                } as MappedLink;
            });

            const { value } = destination;
            const menuUrl = getPageLinkByNodeId(state, value)?.url || value;

            mappedLinks.push({
                title: overriddenTitle || translate("Menu"),
                children: subLinks,
                childrenType: "CascadingMenu",
                url: menuUrl,
                maxDepth: 1,
            });
        } else {
            const linkMapped = getMappedLink(state, maxDepth, overriddenTitle, linkType, destination, openInNewWindow);
            linkMapped && mappedLinks.push(linkMapped);
        }
    }
    return {
        links: mappedLinks,
        categoryIdsToLoad,
        categoryErrorStatusCodeById: state.data.categories.errorStatusCodeById,
        categoryIdsCalled: state.data.categories.categoryIdsCalled,
        // XNG-Change
        xGoogleAnalytics: (state as xGoogleAnalyticsState).xGoogleAnalytics,
        pagePath: getLocation(state).pathname,
    };
};
// <XNG-Change>
export const getMappedLink = (
    state: ApplicationState,
    maxDepth: number,
    overriddenTitle: string,
    linkType: string,
    destination: LinkFieldValue,
    openInNewWindow: boolean,
) => {
    const categoryIdsToLoad: Dictionary<number> = {};

    const { type, value } = destination;

    const depthToLoad = maxDepth;

    let mappedLink: MappedLink | undefined;

    if (type === "Url") {
        mappedLink = {
            url: value,
            title: overriddenTitle,
        };
    } else if (type === "Page") {
        const readonlyPageLink = getPageLinkByNodeId(state, value);
        if (readonlyPageLink) {
            mappedLink = {
                ...readonlyPageLink,
                children: readonlyPageLink.children?.filter(c => !c.excludeFromNavigation),
            };
            if (overriddenTitle) {
                mappedLink.title = overriddenTitle;
            }
        }
    } else if (type === "Category") {
        mappedLink = setupCategoryLink(
            state,
            value,
            categoryIdsToLoad,
            depthToLoad,
            overriddenTitle,
            state.data.categories.categoryIdsCalled,
        );
    }

    if (!mappedLink) {
        return;
    }

    mappedLink.openInNewWindow = openInNewWindow;
    mappedLink.maxDepth = maxDepth;
    mappedLink.isCategory = type === "Category";

    if (linkType === "CascadingMenu") {
        mappedLink.childrenType = linkType;
    }
    return mappedLink;
};

export const getParameter = (categoryId: string, maxDepth: number) => ({
    maxDepth,
    startCategoryId: categoryId === emptyGuid ? undefined : categoryId,
    includeStartCategory: categoryId !== emptyGuid,
});

const setupCategoryLink = (
    state: ApplicationState,
    value: string,
    categoryIdsToLoad: Dictionary<number>,
    maxDepth: number,
    overriddenTitle: string,
    categoryIdsCalled: string[],
) => {
    let mappedLink: MappedLink | undefined;

    if (!value) {
        return;
    }

    const depthLoaded = getCategoryDepthLoaded(state, value);
    const subCategoryIds = getSubCategoryIds(state, value);
    if (
        (depthLoaded < maxDepth || (!subCategoryIds && !categoryIdsCalled?.includes(value))) &&
        !getCategoriesDataView(state, getParameter(value, maxDepth)).isLoading
    ) {
        categoryIdsToLoad[value] = maxDepth;
    } else {
        if (value === emptyGuid) {
            const categoryListPage = getPageLinkByPageType(state, "CategoryListPage");
            mappedLink = {
                url: categoryListPage?.url || "",
                title: overriddenTitle || translate("Products"),
            };
        } else {
            const categoryState = getCategoryState(state, value);
            if (!categoryState.value) {
                if (
                    !categoryState.isLoading &&
                    !getCategoriesDataView(state, getParameter(value, maxDepth)).isLoading
                ) {
                    categoryIdsToLoad[value] = maxDepth;
                }
                return;
            }
            mappedLink = {
                url: categoryState.value.path,
                title: overriddenTitle || categoryState.value.shortDescription,
            };
        }

        loadSubCategories(mappedLink, subCategoryIds, 1, maxDepth, state);
    }

    return mappedLink;
};

const loadSubCategories = (
    mappedLink: MappedLink,
    subCategoryIds: readonly string[] | undefined,
    currentDepth: number,
    maxDepth: number,
    state: ApplicationState,
) => {
    if (currentDepth > maxDepth) {
        return;
    }
    mappedLink.children = [];
    if (!subCategoryIds) {
        return;
    }
    for (const subCategoryId of subCategoryIds) {
        const subCategory = getCategoryState(state, subCategoryId).value;
        if (!subCategory) {
            Logger.error(`Category ${subCategoryId} was not found in data state, excluding from navigation.`);
            continue;
        }
        const childLink: MappedLink = {
            url: subCategory.path,
            title: subCategory.shortDescription,
            children: [],
        };
        mappedLink.children.push(childLink);

        loadSubCategories(childLink, subCategory.subCategoryIds, currentDepth + 1, maxDepth, state);
    }
};

const mapDispatchToProps = {
    loadCategories,
    setFlyoutDrawerIsOpen,
    setNavDrawerIsOpen,
};

// </XNG-Change>

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface HeaderLinkListStyles {
    headerLinkListWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    // XNG-Change
    menuLink?: LinkPresentationProps;
    menuItem?: InjectableCss;
    menuContent?: InjectableCss;
    menuContentLink?: LinkPresentationProps;
    tooltipStyles?: FloatingTooltipStyles;
}

export const headerLinkListStyles: HeaderLinkListStyles = {
    headerLinkListWrapper: {
        css: css`
            text-align: right;
            padding: 0 0 23px 0;
            margin-right: -9px;
        `,
    },
    link: {
        typographyProps: {
            variant: "headerTertiary",
            css: css`
                margin: 0 9px 0 9px;
                display: inline-block;
                &&& {
                    color: ${getColor("text.main")};
                }
            `,
        },
    },
};

const defaultCustomCss = `.list-wrapper {
}

.list-link {
}
`;

export class HeaderLinkList extends React.Component<Props, any, any> {
    // XNG-Changes: Add state for tooltip management
    state = {
        openTooltipIndex: -1,
    };

    UNSAFE_componentWillMount(): void {
        this.loadCategoriesIfNeeded();
    }

    componentDidUpdate(prevProps: Readonly<Props>): void {
        if (!isEqual(this.props.categoryIdsToLoad, prevProps.categoryIdsToLoad)) {
            this.loadCategoriesIfNeeded(true);
        }
    }

    loadCategoriesIfNeeded(isComponentDidUpdate = false) {
        const { categoryIdsToLoad } = this.props;
        if (categoryIdsToLoad) {
            for (const categoryId of Object.keys(categoryIdsToLoad)) {
                if (!this.props.categoryErrorStatusCodeById?.[categoryId]) {
                    const maxDepth = categoryIdsToLoad[categoryId];
                    if (!this.props.categoryIdsCalled?.includes(categoryId) || isComponentDidUpdate) {
                        this.props.loadCategories(getParameter(categoryId, maxDepth));
                    }
                }
            }
        }
    }

    closeNavigationDrawer = () => {
        const { setFlyoutDrawerIsOpen, setNavDrawerIsOpen } = this.props;
        setFlyoutDrawerIsOpen({ isOpen: false });
        setTimeout(() => setFlyoutDrawerIsOpen({ isOpen: undefined }), 300);
        setNavDrawerIsOpen({ navDrawerIsOpen: false });
        setTimeout(() => setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
    };

    // XNG-Changes: Handle tooltip open/close
    handleTooltipOpenChange = (index: number, open: boolean) => {
        this.setState({ openTooltipIndex: open ? index : -1 });
        if (open) {
            this.closeNavigationDrawer();
        }
    };

    render() {
        const { links, fields, extendedStyles } = this.props;
        const { openTooltipIndex } = this.state;
        const styles = headerLinkListStyles;

        if (links.length < 1) {
            return null;
        }

        const customCssWrapper = {
            css: css`
                width: 100%;
                ${fields.customCSS}
            `,
        };

        // XNG-Changes
        const headerLinkListClickHandler = (url?: string, label?: string) => {
            const { xGoogleAnalytics, fields, pagePath } = this.props;

            if (!fields.trackToolBarNavEvent || !xGoogleAnalytics.toolBarNavEvent || !url) {
                return;
            }

            const dataLayer = getDataLayer();
            if (!dataLayer) {
                return;
            }

            const googleAnalyticsData = {
                event: xGoogleAnalytics.toolBarNavEventName,
                location: pagePath,
                click_text: label || "",
                affiliation: xGoogleAnalytics.affiliation || "",
                sales_rep: xGoogleAnalytics.salesRep || "",
                is_rep: xGoogleAnalytics.isRep || "",
                bill_to_num: xGoogleAnalytics.billToNum || "",
                ship_to_num: xGoogleAnalytics.shipToNum || "",
                cust_type: xGoogleAnalytics.customerType || "",
                login_type: xGoogleAnalytics.loginType || "",
                warehouse: xGoogleAnalytics.warehouse || "",
            };

            dataLayer.push({ ecommerce: null });
            dataLayer.push(googleAnalyticsData);
        };

        const MenuLink = ({
            expanded,
            link,
            isCascading,
            index,
        }: {
            expanded?: boolean;
            link: any;
            isCascading?: boolean;
            index: number;
        }) => (
            <Link
                className="list-link"
                key={`${link.url}.${link.title}`}
                href={link.url}
                target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                {...(isCascading ? styles.menuLink : styles.link)}
                aria-expanded={expanded}
                // XNG-Change
                onClick={() =>
                    headerLinkListClickHandler(link.url, fields.links[index].fields.overriddenTitle || link.title)
                }
            >
                {/* XNG-Changes: add overriddenTitle */}
                {fields.links[index].fields.overriddenTitle || link.title}
            </Link>
        );
        return (
            <StyledWrapper {...customCssWrapper}>
                <StyledWrapper className="list-wrapper" {...styles.headerLinkListWrapper}>
                    {links &&
                        links.map((link, index) => {
                            const hasChildren = link.children && link.children.length > 0;
                            const isCascading = hasChildren && link.childrenType === "CascadingMenu";
                            const parentBoxRef = React.createRef<HTMLDivElement>();
                            let menuItem;
                            if (isCascading && link.children) {
                                const menuProps: { maxDepth?: number } = {};
                                if (link.maxDepth) {
                                    menuProps.maxDepth = link.maxDepth;
                                }
                                const menuContent = (
                                    <FancyScroll refElement={parentBoxRef}>
                                        <StyledWrapper {...styles.menuContent} ref={parentBoxRef}>
                                            {link.children.map((child, index) => (
                                                <Link
                                                    key={`${child.url}${index}`}
                                                    href={child.url}
                                                    // XNG-Change
                                                    target={child.openInNewWindow ? "_blank" : ""}
                                                    {...styles.menuContentLink}
                                                    aria-label={child.title}
                                                    onClick={() => headerLinkListClickHandler(child.url, child.title)}
                                                >
                                                    {child.title}
                                                </Link>
                                            ))}
                                        </StyledWrapper>
                                    </FancyScroll>
                                );
                                menuItem = (
                                    // XNG-Changes
                                    <FloatingTooltip
                                        triggerComponent={
                                            <MenuLink
                                                isCascading
                                                expanded={openTooltipIndex === index}
                                                index={index}
                                                link={link}
                                            />
                                        }
                                        isOpen={openTooltipIndex === index}
                                        onOpenChange={open => this.handleTooltipOpenChange(index, open)}
                                        placement="bottom-start"
                                        offset={{ mainAxis: 10, crossAxis: 0 }} // Converted from [0, 10]
                                        trigger="hover"
                                        showArrow={false}
                                        overlay={false}
                                        extendedStyles={styles.tooltipStyles}
                                    >
                                        {menuContent}
                                    </FloatingTooltip>
                                );
                            } else {
                                menuItem = <MenuLink index={index} link={link} />;
                            }
                            return (
                                <StyledWrapper className="item-wrapper" key={index}>
                                    {menuItem}
                                </StyledWrapper>
                            );
                        })}
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(HeaderLinkList),
    definition: {
        group: "Header",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }

                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                // XNG-Change: add cascading menu option
                fieldDefinitions: [
                    {
                        name: "linkType",
                        editorTemplate: "RadioButtonsField",
                        defaultValue: "Link",
                        isRequired: true,
                        options: [
                            {
                                displayName: "Link",
                                value: "Link",
                            },
                            {
                                displayName: "Cascading Menu",
                                value: "CascadingMenu",
                            },
                            {
                                displayName: "Link List",
                                value: "LinkList",
                            },
                        ],
                    },
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                        // XNG-Change
                        allowUrls: item => ["Link", "LinkList"].includes(item.fields.linkType),
                        allowCategories: item => item.fields.linkType !== "LinkList",
                    },
                    // XNG-Change: Add ability for external urls
                    {
                        name: fields.subLinkList,
                        editorTemplate: "ListField",
                        defaultValue: [],
                        getDisplay: item => {
                            return item.fields.subLinkName || "";
                        },
                        isVisible: (item: HasFields) => item.fields.linkType === "LinkList",
                        fieldDefinitions: [
                            {
                                name: subLinkFields.subLinkName,
                                displayName: "Display name",
                                editorTemplate: "TextField",
                                defaultValue: "",
                            },
                            {
                                name: subLinkFields.subLink,
                                displayName: "Link",
                                editorTemplate: "LinkField",
                                defaultValue: {
                                    value: "",
                                    type: "Url",
                                },
                                isRequired: true,
                                allowCategories: item => false,
                            },
                            {
                                name: subLinkFields.openInNewWindow,
                                editorTemplate: "CheckboxField",
                                defaultValue: false,
                            },
                        ],
                    },
                    // XNG-Changes: add overriddenTitle
                    {
                        name: "overriddenTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: false,
                    },
                    {
                        name: "maxDepth",
                        editorTemplate: "DropDownField",
                        defaultValue: 3,
                        options: [{ value: 1 }, { value: 2 }, { value: 3 }, { value: 4 }],
                        isRequired: true,
                        isVisible: (item: HasFields) => item.fields.linkType === "CascadingMenu",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        isVisible: (item: HasFields) => item.fields.linkType === "Link",
                    },
                ],
                tab: basicTab,
            },
            // XNG-Change
            {
                name: fields.trackToolBarNavEvent,
                displayName: "Track Tool Bar Nav Event",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 2,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
